import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import type { Student } from '@/types';
import StudentSummaryHeader from './components/StudentSummaryHeader';
import ExamRecordSection from './components/ExamRecordSection';
import ExamScoreSection from './components/ExamScoreSection';
import ExamScheduleSection from './components/ExamScheduleSection';
import TuitionSection from './components/TuitionSection';
import LessonLogSection from './components/LessonLogSection';
import MakeupSection from './components/MakeupSection';
import ProgressSection from './components/ProgressSection';
import ParentShareSection from './components/ParentShareSection';
import StudentStatusSection from './components/StudentStatusSection';

type TabKey = 'lessons' | 'makeup' | 'progress' | 'examRecords' | 'examScores' | 'schedules' | 'tuition' | 'share';

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: 'lessons', label: '수업일지', icon: 'ri-book-2-line' },
  { key: 'makeup', label: '보충 수업', icon: 'ri-calendar-schedule-line' },
  { key: 'progress', label: '남은 진도', icon: 'ri-route-line' },
  { key: 'examRecords', label: '학교기출 결과', icon: 'ri-file-list-3-line' },
  { key: 'examScores', label: '성적 관리', icon: 'ri-bar-chart-box-line' },
  { key: 'schedules', label: '시험 일정', icon: 'ri-calendar-event-line' },
  { key: 'tuition', label: '교습비', icon: 'ri-wallet-3-line' },
  { key: 'share', label: '학부모 공유', icon: 'ri-share-forward-line' },
];

export default function StudentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [student, setStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<TabKey>('lessons');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('students')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (fetchError) throw fetchError;
      if (!data) {
        setError('학생 정보를 찾을 수 없어요.');
        return;
      }
      setStudent(data as Student);
    } catch {
      setError('학생 정보를 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-foreground-500">
        <i className="ri-loader-4-line mr-2 animate-spin text-xl"></i>
        불러오는 중...
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-16 md:px-6">
        <div className="flex flex-col items-center gap-3 rounded-xl border border-background-200 bg-background-100 py-16">
          <i className="ri-error-warning-line text-2xl text-accent-600"></i>
          <p className="text-sm text-foreground-700">{error || '학생 정보를 찾을 수 없어요.'}</p>
          <button
            type="button"
            onClick={load}
            className="rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 md:px-6 md:py-10">
      <StudentSummaryHeader student={student} onUpdated={setStudent} />

      <div className="no-scrollbar mt-6 overflow-x-auto">
        <div className="inline-flex gap-1 rounded-full bg-background-100 p-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                tab === t.key
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className={t.icon}></i>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        {tab === 'lessons' && <LessonLogSection studentId={student.id} />}
        {tab === 'makeup' && <MakeupSection studentId={student.id} />}
        {tab === 'progress' && <ProgressSection studentId={student.id} />}
        {tab === 'examRecords' && <ExamRecordSection studentId={student.id} />}
        {tab === 'examScores' && <ExamScoreSection studentId={student.id} />}
        {tab === 'schedules' && <ExamScheduleSection studentId={student.id} />}
        {tab === 'tuition' && <TuitionSection student={student} onUpdated={setStudent} />}
        {tab === 'share' && <ParentShareSection student={student} />}
      </div>

      <StudentStatusSection student={student} onUpdated={setStudent} />
    </div>
  );
}